import { Check, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router";

import { useGetSubscriptionsQuery } from "@/api/api";
import { useSpace } from "@/api/hooks";
import { HGUser } from "@/common/types";
import { userPath } from "@/common/urls";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import FollowButton from "./FollowButton";
import UserAvatar from "./UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { SidebarMenuButton } from "../sidebar";

/**
 * The follow control for the space being browsed.
 *
 * Sits under the switcher rather than on it: the header band is the identity,
 * and crowding an action into it leaves neither enough room to read cleanly.
 * Renders nothing in your own space — there is no one to follow there.
 */
export function SpaceFollowBanner() {
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();

  if (!me || isOwn || !owner) return null;

  return (
    <div className="flex shrink-0 justify-end px-2 pt-2">
      <FollowButton user={owner} />
    </div>
  );
}

/**
 * Which person's collections the sidebar is showing, and the way to change it.
 *
 * Subscriptions used to be a tab sitting beside Collections, but the two are
 * not peers: choosing a person *scopes* the collection list. As a switcher the
 * list below simply reloads in place, so picking someone no longer strands you
 * on the wrong tab.
 */
export default function SpaceSwitcher() {
  const navigate = useNavigate();
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();
  const { data: subscriptions } = useGetSubscriptionsQuery(undefined, {
    skip: !me,
  });

  // Own name and avatar come from `me`, i.e. from the API. They used to come
  // from the decoded Google ID token in localStorage, which is empty for a
  // password account.
  const label = isOwn ? me?.name || "Your collections" : owner?.name || "Collections";
  const picture = isOwn ? me?.picture : owner?.picture;
  // Initials come from the person, not the label: deriving them from
  // "Your collections" would put "YC" on an unnamed account.
  const avatarName = isOwn
    ? me?.name || me?.nickname?.nickname || me?.id
    : owner?.name || owner?.nickname;

  const row = (
    <>
      <UserAvatar name={avatarName} picture={picture} className="size-6" />
      {/* min-w-0 so a long name ellipsises instead of shoving the chevron out */}
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">
        {label}
      </span>
    </>
  );

  // Signed out there is nothing to switch between: show whose page this is and
  // leave it at that.
  if (!me) {
    if (!owner) return null;
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2 px-1 text-foreground">
        {row}
      </div>
    );
  }

  const goTo = (path: string) => () => navigate(path);

  const followed: HGUser[] = subscriptions ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          aria-label="Switch collections owner"
          className="h-9 px-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          {row}
          <ChevronDown className="ml-auto size-4 shrink-0 opacity-60" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
      >
        <DropdownMenuItem onClick={goTo("/")}>
          <UserAvatar
            name={me?.name || me?.nickname?.nickname || me?.id}
            picture={me?.picture}
            className="size-5"
          />
          <span className="min-w-0 flex-1 truncate">
            {me?.name || "Your collections"}
          </span>
          {isOwn && <Check className="size-4 shrink-0" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {followed.length === 0 ? (
          <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
            You&rsquo;re not following anyone yet.
          </div>
        ) : (
          <>
            <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-[.1em] text-muted-foreground">
              Following
            </DropdownMenuLabel>
            {followed.map((sub) => (
              <DropdownMenuItem key={sub.id} onClick={goTo(userPath(sub))}>
                <UserAvatar
                  name={sub.name || sub.nickname}
                  picture={sub.picture}
                  className="size-5"
                />
                <span className="min-w-0 flex-1 truncate">
                  {sub.name || `User #${sub.id}`}
                </span>
                {owner?.id === sub.id && (
                  <Check className="size-4 shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
